import { motion } from 'framer-motion'
import React from 'react'

import { SCREENS } from 'components/Editor/DeviceEmulator/constants'
import { TooltipCourse } from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import { muteToggle } from 'components/MediaPlayer/store/actions'
import type { Actions } from 'components/MediaPlayer/store/types'
import { MediaPlayerMotionButton } from 'components/MediaPlayer/styled/MediaPlayerButton'
import type { MediaStyles } from 'components/MediaPlayer/types'
import { stopPropagation } from 'constants/commonConstans'

import { MediaControlType } from '../types'
import { useMediaT } from '../useMediaT'
import {
  VolumeSliderFilled,
  VolumeSliderInput,
  VolumeSliderInputWrapper,
  VolumeSliderTrack,
  VolumeSliderWrapper,
} from './styled'

interface IVolumeSliderProps {
  onChange: (volume: number) => void
  dispatch: React.Dispatch<Actions>
  value: number
  container: () => HTMLElement
  muted?: boolean
  styles?: Partial<Record<MediaStyles, React.CSSProperties>>
  hiddenSlider?: boolean
  isCourse?: boolean
}

const LINE_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
}

const PATH_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
}

const VolumeSlider: React.FC<IVolumeSliderProps> = ({
  onChange,
  value,
  styles,
  muted,
  dispatch,
  container,
  hiddenSlider,
  isCourse = false,
}) => {
  const isMobile = window.innerWidth <= SCREENS.mobile
  const tt = useMediaT(isCourse)

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange(parseFloat(event.target.value))

  const handleKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      const keyDownStep = event.key === 'ArrowLeft' ? -0.05 : 0.05
      if (event.key === 'ArrowLeft') {
        onChange(Math.max(value + keyDownStep, 0))
      } else {
        onChange(Math.min(value + keyDownStep, 1))
      }
    }
  }

  if (isMobile) {
    return null
  }

  const isMuted = value === 0 || muted
  const valueLocal = isMuted ? 0 : value

  return (
    <VolumeSliderWrapper>
      <TooltipCourse
        container={container()}
        overlay={isMuted ? tt('elements.media.unmute') : tt('elements.media.mute')}
        placement='top'
      >
        <MediaPlayerMotionButton
          $styles={styles}
          aria-keyshortcuts='m'
          aria-label='Mute'
          aria-pressed={isMuted}
          data-player-control={MediaControlType.Mute}
          onClick={() => dispatch(muteToggle())}
          transition={{ duration: 0.3 }}
        >
          <motion.svg
            fill='none'
            style={{ height: '20px', width: '20px' }}
            viewBox='0 0 20 20'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden
          >
            {isMuted ? (
              <>
                <motion.line
                  animate='visible'
                  initial='hidden'
                  stroke='currentColor'
                  strokeWidth='1'
                  variants={LINE_VARIANTS}
                  x1='12'
                  x2='16'
                  y1='8'
                  y2='12'
                />
                <motion.line
                  animate='visible'
                  initial='hidden'
                  stroke='currentColor'
                  strokeWidth='1'
                  variants={LINE_VARIANTS}
                  x1='16'
                  x2='12'
                  y1='8'
                  y2='12'
                />
              </>
            ) : (
              <>
                <motion.path
                  animate='visible'
                  d='M12.5002 8.33331V11.6666'
                  initial='hidden'
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  variants={PATH_VARIANTS}
                />
                <motion.path
                  animate='visible'
                  d='M15.0002 6.66669V13.3334'
                  initial='hidden'
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  variants={PATH_VARIANTS}
                />
              </>
            )}
            <motion.path
              clipRule='evenodd'
              d='M4.896 7.18753H2.91683C2.45683 7.18753 2.0835 7.56086 2.0835 8.02086V11.9792C2.0835 12.4392 2.45683 12.8125 2.91683 12.8125H4.896L8.21183 15.6217C8.7535 16.0809 9.5835 15.6959 9.5835 14.9859V5.01419C9.5835 4.30419 8.75266 3.91919 8.21183 4.37836L4.896 7.18753Z'
              fillRule='evenodd'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </motion.svg>
        </MediaPlayerMotionButton>
      </TooltipCourse>
      {!hiddenSlider && (
        <VolumeSliderInputWrapper>
          <VolumeSliderTrack $styles={styles}>
            <VolumeSliderFilled $styles={styles} $value={valueLocal * 100} />
          </VolumeSliderTrack>
          <VolumeSliderInput
            aria-label='Volume control'
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={Math.round(valueLocal * 100)}
            aria-valuetext={`${Math.round(valueLocal * 100)}%`}
            data-player-control={MediaControlType.Volume}
            max={1}
            min={0}
            onChange={handleVolumeChange}
            onKeyDown={handleKeydown}
            onMouseDown={stopPropagation}
            step={0.01}
            type='range'
            value={valueLocal}
          />
        </VolumeSliderInputWrapper>
      )}
    </VolumeSliderWrapper>
  )
}

export default VolumeSlider
